import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { type PlatformType, platformTypes } from '@backend/lib/db/schema/platform'
import { INSTAGRAM_RESTRICTIONS } from '@backend/lib/platforms/instagram/lib/restrictions'
import { LINKEDIN_RESTRICTIONS } from '@backend/lib/platforms/linkedin/lib/restrictions'
import { TIKTOK_RESTRICTIONS } from '@backend/lib/platforms/tiktok/lib/restrictions'
import { YOUTUBE_RESTRICTIONS } from '@backend/lib/platforms/youtube/lib/restrictions'
import { FACEBOOK_RESTRICTIONS } from '@backend/lib/platforms/facebook/lib/restrictions'

/**
 * Post type definitions
 */
export type PostType =
  | { type: 'empty' }
  | { type: 'text-only' }
  | { type: 'single-image' }
  | { type: 'single-video' }
  | { type: 'multi-image'; count: number }
  | { type: 'multi-media'; images: number; videos: number }
  | { type: 'unsupported'; reason: string }

export type PlatformCompatibilityEntry = { compatible: boolean; reason?: string }
export type PlatformCompatibility = Record<PlatformType, PlatformCompatibilityEntry>
export type CaptionLengths = Record<PlatformType, number>

/** Empty (all-true) compatibility map — useful as a starting point for accumulating issues. */
const emptyCompatibility = (): PlatformCompatibility =>
  Object.fromEntries(platformTypes.map((p) => [p, { compatible: true }])) as PlatformCompatibility

export const countMediaByType = (
  media: WorkspaceMedia[],
): { images: number; videos: number; documents: number; canvases: number } => {
  const images = media.filter((m) => m.type === 'image').length
  const videos = media.filter((m) => m.type === 'video').length
  const documents = media.filter((m) => m.type === 'document').length
  const canvases = media.filter((m) => m.type === 'canvas').length

  return { images, videos, documents, canvases }
}

export const analyzePostType = (media: WorkspaceMedia[], hasCaption: boolean): PostType => {
  if (media.length === 0) {
    if (hasCaption) {
      return { type: 'text-only' }
    }
    return { type: 'empty' }
  }

  if (media.length === 1) {
    const item = media[0]!
    if (item.type === 'image' || item.type === 'canvas') return { type: 'single-image' }
    if (item.type === 'video') return { type: 'single-video' }
    if (item.type === 'document') {
      return { type: 'unsupported', reason: 'Documents are not supported yet' }
    }
  }

  const { images, videos, documents, canvases } = countMediaByType(media)
  const totalImages = images + canvases // Treat canvas as image

  if (documents > 0) {
    return { type: 'unsupported', reason: 'Documents are not supported yet' }
  }

  if (videos > 1 && totalImages === 0) {
    return { type: 'multi-media', images: totalImages, videos }
  }

  if (videos > 0 && totalImages > 0) {
    return { type: 'multi-media', images: totalImages, videos }
  }

  if (videos === 1 && totalImages === 0) {
    return { type: 'single-video' }
  }

  if (totalImages > 0 && videos === 0) {
    return { type: 'multi-image', count: totalImages }
  }

  return { type: 'unsupported', reason: 'This media combination is not supported' }
}

/** Per-platform caption hard limits. Adding a platform = adding one entry. */
const CAPTION_MAX_LENGTH: Record<PlatformType, number> = {
  instagram: INSTAGRAM_RESTRICTIONS.caption.maxLength,
  linkedin: LINKEDIN_RESTRICTIONS.caption.maxLength,
  tiktok: TIKTOK_RESTRICTIONS.caption.maxLength,
  youtube: YOUTUBE_RESTRICTIONS.metadata.description.maxLength,
  facebook: FACEBOOK_RESTRICTIONS.caption.maxLength,
  // Generation-only target; no platform module. Meta ad primary text practical max.
  'meta-ads': 2200,
}

/** Per-platform user-facing label for the caption-too-long message. */
const CAPTION_TOO_LONG_LABEL: Record<PlatformType, string> = {
  instagram: 'Caption',
  linkedin: 'Caption',
  tiktok: 'Caption',
  youtube: 'Description',
  facebook: 'Caption',
  'meta-ads': 'Ad copy',
}

export const checkCompatibility = (
  postType: PostType,
  captionLength: CaptionLengths,
): PlatformCompatibility => {
  const result = emptyCompatibility()

  // Check caption length limits for all platforms
  for (const platform of platformTypes) {
    const len = captionLength[platform]
    const max = CAPTION_MAX_LENGTH[platform]
    if (len > max) {
      const label = CAPTION_TOO_LONG_LABEL[platform]
      result[platform] = {
        compatible: false,
        reason: `${label} too long (${len}/${max} characters). Shorten your ${label.toLowerCase()}`,
      }
    }
  }

  // LinkedIn always requires a caption (except for empty posts which are handled below)
  if (postType.type !== 'empty' && captionLength.linkedin === 0) {
    result.linkedin.compatible = false
    result.linkedin.reason = 'Write a caption for your post'
  }

  // Check post type specific requirements
  switch (postType.type) {
    case 'empty':
      result.instagram.compatible = false
      result.instagram.reason =
        'Instagram requires at least 1 image/video to post. Add an image or video'
      result.linkedin.compatible = false
      result.linkedin.reason = 'Add media or write a caption'
      result.tiktok.compatible = false
      result.tiktok.reason = 'TikTok requires a video to post. Add a video'
      result.youtube.compatible = false
      result.youtube.reason = 'YouTube requires a video to post. Add a video'
      result.facebook.compatible = false
      result.facebook.reason = 'Add media or write a caption'
      break

    case 'text-only':
      result.instagram.compatible = false
      result.instagram.reason =
        'Instagram requires at least 1 image/video to post. Add an image or video'
      result.tiktok.compatible = false
      result.tiktok.reason = 'TikTok requires a video to post. Add a video'
      result.youtube.compatible = false
      result.youtube.reason = 'YouTube requires a video to post. Add a video'
      // LinkedIn and Facebook both allow text-only posts.
      break

    case 'single-image':
      // TikTok supports single-image photo posts (carousel of 1) via the
      // content-init endpoint. YouTube remains video-only.
      result.youtube.compatible = false
      result.youtube.reason = 'YouTube only supports videos. Add a video instead'
      break

    case 'single-video':
      // All platforms support single video
      break

    case 'multi-media':
      // Mixed content — user selects post vs reel at preview time
      result.linkedin.compatible = false
      result.linkedin.reason = 'LinkedIn does not support mixed content'
      result.tiktok.compatible = false
      result.tiktok.reason = 'TikTok only supports single videos'
      result.youtube.compatible = false
      result.youtube.reason = 'YouTube only supports single videos'
      result.facebook.compatible = false
      result.facebook.reason = 'Facebook does not support mixed image+video posts'
      break

    case 'multi-image':
      if (postType.count > INSTAGRAM_RESTRICTIONS.carousel.maxItems) {
        result.instagram.compatible = false
        result.instagram.reason = `Too many images (${postType.count}/${INSTAGRAM_RESTRICTIONS.carousel.maxItems} max). Remove some images`
      }
      if (postType.count > LINKEDIN_RESTRICTIONS.multiImage.maxItems) {
        result.linkedin.compatible = false
        result.linkedin.reason = `Too many images (${postType.count}/${LINKEDIN_RESTRICTIONS.multiImage.maxItems} max). Remove some images`
      }
      if (postType.count > FACEBOOK_RESTRICTIONS.multiPhoto.maxItems) {
        result.facebook.compatible = false
        result.facebook.reason = `Too many images (${postType.count}/${FACEBOOK_RESTRICTIONS.multiPhoto.maxItems} max). Remove some images`
      }
      if (postType.count > TIKTOK_RESTRICTIONS.photo.maxItems) {
        result.tiktok.compatible = false
        result.tiktok.reason = `Too many images (${postType.count}/${TIKTOK_RESTRICTIONS.photo.maxItems} max). Remove some images`
      }
      // YouTube doesn't support multi-image — TikTok now does (photo carousel).
      result.youtube.compatible = false
      result.youtube.reason = 'YouTube only supports single videos'
      break

    case 'unsupported':
      result.instagram.compatible = false
      result.instagram.reason = postType.reason
      result.linkedin.compatible = false
      result.linkedin.reason = postType.reason
      result.tiktok.compatible = false
      result.tiktok.reason = postType.reason
      result.youtube.compatible = false
      result.youtube.reason = postType.reason
      result.facebook.compatible = false
      result.facebook.reason = postType.reason
      break
  }

  return result
}

export const getPostTypeBadge = (postType: PostType): string => {
  switch (postType.type) {
    case 'empty':
    case 'text-only':
    case 'single-image':
    case 'multi-image':
    case 'multi-media':
    case 'unsupported':
      return 'Post'
    case 'single-video':
      return 'Reel'
  }
}

export type MediaValidationResult = { allowed: true } | { allowed: false; reason: string }

export const canAddMedia = (
  currentMedia: WorkspaceMedia[],
  newMediaType: WorkspaceMedia['type'],
): MediaValidationResult => {
  const { images, canvases } = countMediaByType(currentMedia)
  const totalImages = images + canvases

  const addingImageType = newMediaType === 'image' || newMediaType === 'canvas'
  const addingVideoType = newMediaType === 'video'

  if (newMediaType === 'document') {
    return {
      allowed: false,
      reason: 'This document type is not supported. PDFs are automatically converted to images.',
    }
  }

  if (currentMedia.length === 0) {
    return { allowed: true }
  }

  // Adding video
  if (addingVideoType) {
    return { allowed: true }
  }

  if (addingImageType) {
    // Take the minimum across every platform that supports multi-image so
    // bumping any one platform's cap can never silently outrun another.
    const maxImages = Math.min(
      INSTAGRAM_RESTRICTIONS.carousel.maxItems,
      LINKEDIN_RESTRICTIONS.multiImage.maxItems,
      FACEBOOK_RESTRICTIONS.multiPhoto.maxItems,
    )

    if (totalImages >= maxImages) {
      return {
        allowed: false,
        reason: `Maximum ${maxImages} images allowed`,
      }
    }

    return { allowed: true }
  }

  return { allowed: false, reason: 'Invalid media type' }
}

export const canAddMultipleMedia = (
  currentMedia: WorkspaceMedia[],
  newMediaTypes: WorkspaceMedia['type'][],
): MediaValidationResult => {
  const uniqueTypes = [...new Set(newMediaTypes)]

  if (uniqueTypes.length > 1) {
    const onlyImagesAndCanvas = uniqueTypes.every((t) => t === 'image' || t === 'canvas')
    if (!onlyImagesAndCanvas) {
      return { allowed: false, reason: 'Cannot upload mixed media types' }
    }
  }

  let tempMedia = [...currentMedia]
  for (const mediaType of newMediaTypes) {
    const validation = canAddMedia(tempMedia, mediaType)
    if (!validation.allowed) {
      return validation
    }
    const mockMediaType = mediaType === 'canvas' ? 'canvas' : mediaType
    tempMedia = [...tempMedia, { type: mockMediaType } as WorkspaceMedia]
  }

  return { allowed: true }
}
